import { ChevronRight, History, LayoutGrid, Trophy } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { GAMES, type GameInfo } from '../lib/games'
import { GameLogo } from './art/GameLogo'
import { BottomSheet } from './BottomSheet'
import { SoundToggle } from './SoundToggle'

interface Props {
  open: boolean
  onClose: () => void
  /** The game whose home is showing; it is left out of "Switch game". */
  current: GameInfo['to']
}

/** The phone "Menu" tab: other games, the arena pages and the sound switch. */
export function MenuSheet({ open, onClose, current }: Props) {
  const others = GAMES.filter((g) => g.to !== current)
  return (
    <BottomSheet open={open} onClose={onClose} title="Menu">
      <div className="flex flex-col gap-4">
        <section>
          <h3 className="mb-1.5 px-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            Switch game
          </h3>
          <ul className="flex flex-col gap-1">
            {others.map((g) => (
              <li key={g.to}>
                <MenuRow
                  to={g.to}
                  onClick={onClose}
                  icon={<GameLogo game={g.to} className="w-7" compact />}
                >
                  <span className="block font-semibold">{g.title}</span>
                  <span className="block text-xs text-slate-500">{g.tagline}</span>
                </MenuRow>
              </li>
            ))}
            <li>
              <MenuRow
                to="/"
                onClick={onClose}
                icon={<LayoutGrid className="size-5 text-slate-500" />}
              >
                All games
              </MenuRow>
            </li>
          </ul>
        </section>
        <section>
          <h3 className="mb-1.5 px-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            Arena
          </h3>
          <ul className="flex flex-col gap-1">
            <li>
              <MenuRow
                to="/leaderboard"
                onClick={onClose}
                icon={<Trophy className="size-5 text-slate-500" />}
              >
                Leaderboard
              </MenuRow>
            </li>
            <li>
              <MenuRow
                to="/me"
                onClick={onClose}
                icon={<History className="size-5 text-slate-500" />}
              >
                Profile
              </MenuRow>
            </li>
            <li>
              <SoundToggle withLabel />
            </li>
          </ul>
        </section>
      </div>
    </BottomSheet>
  )
}

function MenuRow({
  to,
  onClick,
  icon,
  children,
}: {
  to: string
  onClick: () => void
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
    >
      <span className="flex w-7 shrink-0 items-center justify-center">{icon}</span>
      <span className="min-w-0 flex-1">{children}</span>
      <ChevronRight className="size-4 text-slate-400" aria-hidden />
    </Link>
  )
}
